<template>
    <NuxtLink
        :id="`r-id-${slug || id}`"
        ref="card"
        class="resource-card"
        :class="{
            'resource-card--bottom-overlay': !hideOverlay && !['job', 'org'].includes(imageType),
            'no-scrollbar h-[325px] overflow-scroll': fixedWidth,
            'cursor-default': disabled
        }"
        :target="inNewTab ? '_blank' : '_self'"
        :to="{
            ...to,
            ...returnRoute
        }"
    >
        <div class="resource-card--image">
            <div class="relative w-full overflow-x-hidden">
                <CThumbnailImage
                    :alt="heading"
                    class="aspect-[5/4] rounded-md rounded-b-none"
                    :dummy="dummy"
                    :src="image"
                    :type="imageType"
                />

                <div
                    v-if="
                        (badgeMain?.text && badgeMain?.color) ||
                        badgeText ||
                        badgeTextSuccess ||
                        badgeRecording ||
                        badgeTextInkluzivne
                    "
                    class="absolute inset-x-4 bottom-4"
                >
                    <div v-if="badgeMain?.text && badgeMain?.color" class="mb-1">
                        <CBadge :type="badgeMain.color">{{ badgeMain.text }}</CBadge>
                    </div>
                    <div class="flex flex-wrap gap-1">
                        <CBadge v-if="badgeText">{{ badgeText }}</CBadge>
                        <CBadge v-if="badgeTextSuccess" type="success">{{ badgeTextSuccess }}</CBadge>
                        <CBadge v-if="badgeTextInkluzivne">{{ badgeTextInkluzivne }}</CBadge>
                        <CBadge v-if="badgeRecording" type="red">ZÁZNAM</CBadge>
                    </div>
                </div>
            </div>

            <div v-if="tip" class="absolute inset-x-0 top-0 m-2">
                <CBadge thin-text type="secondary">Tip</CBadge>
            </div>

            <div v-if="!disabled" class="resource-card--overlay">
                <CButton class="resource-card-button" tabindex="-1" variant="primary">
                    {{ buttonText }}
                </CButton>
            </div>

            <div v-if="badgeSide" class="resource-card--side">
                <div class="trapezoid">
                    <div class="trapezoid reverse">
                        <p>{{ badgeSide }}</p>
                    </div>
                </div>
            </div>

            <div v-if="lang && lang !== 'sk'" class="resource-card--lang">
                <p>
                    {{ lang }}
                </p>
            </div>
        </div>

        <div class="resource-card-content">
            <div v-if="heading" class="resource-card-content--name">
                <span class="resource-card-content--name-title break-words">
                    {{ heading }}
                </span>
            </div>

            <span v-if="subHeading" class="resource-card-content--position">
                {{ subHeading }}
            </span>

            <span v-if="text" class="resource-card-content--small-text" :class="{ 'mb-4': !$slots.default }">
                {{ text }}
            </span>

            <span v-if="$slots.default" class="resource-card-content--location">
                <slot />
            </span>

            <div
                v-if="pills && pills.length > 0"
                class="resource-card-content--pills mt-1 flex flex-wrap items-center gap-0.5 pb-2.5"
                @click.prevent
            >
                <component
                    :is="preview ? CTags : CSearchTags"
                    append
                    :highlighted="imageType === 'job' ? highlighted : undefined"
                    :query-search-param="searchTagQueryParam"
                    :search-route-name="searchTagRouteName"
                    :tags="pills.slice(0, tagsMaxCount)"
                    variant="plain"
                />
            </div>

            <div
                v-if="projectType === 'volunteering_week'"
                class="resource-card-content--time -mt-2 flex flex-row items-center gap-1.5"
            >
                <IconTextRow icon="clock" slim> {{ startDateAt }} </IconTextRow>
            </div>

            <div
                v-if="projectType === 'volunteering_week'"
                class="resource-card-content--spots -mt-2 flex flex-row items-center gap-1.5"
            >
                <IconTextRow icon="people" slim> Voľných miest: {{ availableSpots || 0 }} </IconTextRow>
            </div>

            <div v-if="richTextDescription" class="resource-card-content--inner">
                <div class="flex gap-1.5">
                    <CIcon v-if="descriptionIcon" class="size-6 text-gray-840 dark:text-txt-headings" :name="descriptionIcon" />
                    <p>
                        <template v-if="descriptionHeading">
                            <strong>{{ descriptionHeading }}</strong>
                            <br />
                        </template>
                        <span v-html="richTextDescription"></span>
                    </p>
                </div>
            </div>
        </div>
    </NuxtLink>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { BadgeMainType } from '#shared/types/Badge'
import IconTextRow from '@/components/common/IconTextRow.vue'
import CBadge from '@/components/ui/CBadge.vue'
import CButton from '@/components/ui/CButton.vue'
import CIcon from '@/components/ui/CIcon.vue'
import CSearchTags from '@/components/ui/CSearchTags.vue'
import CTags from '@/components/ui/CTags.vue'
import CThumbnailImage from '@/components/ui/CThumbnailImage.vue'

const card = ref(null)
const highlighted = ref(['flexi'] as string[])

const props = defineProps({
    image: {
        type: String,
        default: ''
    },
    tip: {
        type: Boolean,
        default: false
    },
    id: {
        type: String,
        default: ''
    },
    slug: {
        type: String,
        default: ''
    },
    imageType: {
        type: String,
        required: true
    },
    badgeMain: {
        type: Object as () => BadgeMainType,
        default: undefined
    },
    badgeText: {
        type: String,
        default: undefined
    },
    badgeSide: {
        type: String,
        default: ''
    },
    badgeTextSuccess: {
        type: String,
        default: undefined
    },
    badgeRecording: {
        type: String,
        default: undefined
    },
    badgeTextInkluzivne: {
        type: String,
        default: undefined
    },
    to: {
        type: Object as () => RouteLocationRaw,
        default: undefined
    },
    heading: {
        type: String,
        required: true
    },
    subHeading: {
        type: String,
        default: ''
    },
    text: {
        type: String,
        default: ''
    },
    buttonText: {
        type: String,
        default: 'Viac'
    },
    pills: {
        type: Array,
        default: () => []
    },
    tagsMaxCount: {
        type: Number,
        default: 3
    },
    descriptionHeading: {
        type: String,
        default: null
    },
    description: {
        type: String,
        default: null
    },
    descriptionIcon: {
        type: String,
        default: null
    },
    loading: {
        type: Boolean,
        default: false
    },
    lang: {
        type: String,
        default: ''
    },
    returnRoute: {
        type: Object,
        default: undefined
    },
    searchTagRouteName: {
        type: String,
        default: 'vysledky-vyhladavania'
    },
    searchTagQueryParam: {
        type: String,
        default: 'hashtag'
    },
    fixedWidth: {
        type: Boolean,
        default: false
    },
    preview: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    inNewTab: {
        type: Boolean,
        default: false
    },
    dummy: {
        type: Boolean,
        default: false
    },
    hideOverlay: {
        type: Boolean,
        default: false
    },
    availableSpots: {
        type: Number,
        default: null
    },
    projectType: {
        type: String,
        default: null
    },
    maxVolunteerCount: {
        type: Number,
        default: null
    },
    receivedVolunteerRequestsCount: {
        type: String,
        default: null
    },
    startDate: {
        type: [Date, Number],
        required: false,
        default: null
    }
})

// Shows only rich text if description is set.
// Strip tags + decode entities without the DOM so this is SSR-safe and produces
// identical output on server and client (document is undefined during SSR).
const decodeHtmlEntities = (text: string) =>
    text
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#0*39;/g, "'")
        .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))

const richTextDescription = computed(() => {
    if (!props.description) return false

    return decodeHtmlEntities(props.description.replace(/<[^>]*>/g, ''))
})

const volunteerSpots = computed(() => {
    if (props.maxVolunteerCount === null || props.receivedVolunteerRequestsCount === null) {
        return null
    }
    return Math.max(0, props.maxVolunteerCount - Number(props.receivedVolunteerRequestsCount))
})

const startDateAt = computed(() => {
    if (!props.startDate) {
        return '19.05'
    }

    // Convert startDate to Date if it's a timestamp
    const date = typeof props.startDate === 'number' ? new Date(props.startDate) : props.startDate

    // Ensure date is a valid Date object
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        return null // Return null if date is invalid
    }

    // Format the date as "DD.MM o HH:mm"
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')

    return `${day}.${month} o ${hours}:${minutes}`
})
</script>

<style lang="postcss" scoped>
.resource-card {
    --resource-card-bg: white;
    --resource-card-border-radius: 6px;
    --resource-card-inner-spacing-x: 16px;
    --resource-card-inner-spacing-y-top: 24px;
    --resource-card-inner-spacing-y-bottom: 20px;

    display: block;
    border-radius: var(--resource-card-border-radius);
    background-color: var(--resource-card-bg);
    box-shadow: 0 2px 4px 0 #e7e7ea;
    width: 100%;
    position: relative;
    transition: all 0.2s ease-in-out;
    margin-bottom: 20px;
    &:focus {
        box-shadow: 0px 0px 1px 1px #1dc8cc;
        outline: none;
    }
    &.resource-card--bottom-overlay:after {
        content: '';
        position: absolute;
        width: 100%;
        height: 60px;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
        border-bottom-left-radius: var(--resource-card-border-radius);
        border-bottom-right-radius: var(--resource-card-border-radius);
        left: 0;
        bottom: 0;
    }
    &:hover {
        box-shadow:
            0 10px 26px 0 rgba(31, 31, 35, 0.1),
            0 30px 26px 0 rgba(31, 31, 35, 0.1);
        &.resource-card--bottom-overlay:after {
            display: none;
        }
        .resource-card-content--inner {
            overflow: visible;
            z-index: 20;
            div {
                position: absolute;
                z-index: 20;
                min-height: 70px;
                height: auto;
                background-color: var(--resource-card-bg);
                border-bottom-left-radius: var(--resource-card-border-radius);
                border-bottom-right-radius: var(--resource-card-border-radius);
                &:before {
                    content: '';
                    height: 5px;
                    width: 100%;
                    position: absolute;
                    left: 0;
                    top: -5px;
                    background: inherit;
                    z-index: 2;
                }
            }
        }
    }

    &-content {
        padding-top: 16px;
    }
    &-content--name {
        padding-left: var(--resource-card-inner-spacing-x);
        padding-right: var(--resource-card-inner-spacing-x);
    }
    &-content--name-title {
        color: #0d0e2d;
        font-size: 18px;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 20px;
    }
    &--image {
        border-top-left-radius: var(--resource-card-border-radius);
        border-top-right-radius: var(--resource-card-border-radius);
        position: relative;
        display: flex;
    }

    &--lang {
        position: absolute;
        right: 0;
        margin: 1em;
        background: white;
        height: auto;
        text-transform: uppercase;
        top: 0;
        border-radius: 5px;
        border: 1px solid #1dc8cc;

        p {
            margin: 0;
            padding: 0.25em;
            line-height: 16px;
            font-size: 16px;
        }
    }

    &--side {
        position: absolute;
        transform: rotate(90deg) translateY(-100%);
        transform-origin: top left;
        margin: 1em 0;
        left: 100%;
        height: 10px;
        z-index: 2;

        .trapezoid {
            border-bottom: 10px solid #555;
            border-left: 7.5px solid transparent;
            border-right: 7.5px solid transparent;
            height: 0;
            width: calc(180px - 2 * 1em);
            text-align: center;
            position: relative;
            &.reverse {
                border-bottom: 25px solid #1dc8cc;
                border-left: 12.5px solid transparent;
                border-right: 12.5px solid transparent;
                position: absolute;
                width: 100%;
                transform: rotate(180deg);
                color: white;
                white-space: nowrap;
                font-weight: 500;
                font-size: 14px;
                letter-spacing: 1px;
                p {
                    position: relative;
                    bottom: -2.5px;
                }
            }
        }
    }
    &--overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(14, 16, 46, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 100ms ease-in;
        border-top-left-radius: var(--resource-card-border-radius);
        border-top-right-radius: var(--resource-card-border-radius);
        z-index: 0;

        .resource-card:hover & {
            opacity: 1;
        }
    }
    &-content--position {
        display: inline-block;
        width: 100%;
        color: #9fa0ac;
        font-size: 14px;
        letter-spacing: 0;
        line-height: 15px;
        padding-left: var(--resource-card-inner-spacing-x);
        padding-right: var(--resource-card-inner-spacing-x);
    }
    &-content--small-text {
        padding-left: var(--resource-card-inner-spacing-x);
        padding-right: var(--resource-card-inner-spacing-x);
        color: var(--color-resource-card-text-dark);
        font-size: 15px;
        line-height: 18px;
        display: inline-block;
        margin-top: 4px;
    }
    &-content--location {
        display: flex;
        flex-direction: column;
        font-size: 16px;
        letter-spacing: 0;
        line-height: 18px;
        width: 100%;
        padding-top: 11px;
        padding-bottom: 10px;
        padding-left: var(--resource-card-inner-spacing-x);
        padding-right: var(--resource-card-inner-spacing-x);
        .location--icon {
            margin-right: 6px;
        }
    }
    &-content--spots,
    &-content--time {
        display: flex;
        flex-direction: column;
        font-size: 16px;
        letter-spacing: 0;
        line-height: 18px;
        width: 100%;
        padding-bottom: 10px;
        padding-left: var(--resource-card-inner-spacing-x);
        padding-right: var(--resource-card-inner-spacing-x);
        .spots--icon {
            margin-right: 6px;
        }
    }
    &-content--inner {
        width: 100%;
        height: 70px;
        z-index: 0;
        overflow: hidden;
        position: relative;
        div {
            position: absolute;
            width: 100%;
            padding-left: var(--resource-card-inner-spacing-x);
            padding-right: var(--resource-card-inner-spacing-x);
            padding-bottom: var(--resource-card-inner-spacing-y-bottom);
            font-size: 14px;
            letter-spacing: 0;
            line-height: 18px;
            color: var(--color-resource-card-text);
            word-break: break-word;
        }
    }
    &-content--pills {
        margin-bottom: 10px;
        padding-left: var(--resource-card-inner-spacing-x);
        padding-right: var(--resource-card-inner-spacing-x);
    }
    &-content--pills .c-pill {
        margin-bottom: 6px;
    }
}

.no-scrollbar::-webkit-scrollbar {
    display: none;
}

.no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

/* Neutral surfaces follow the selected theme; brand colors and images retain their colors. */
[data-theme="dark"] .resource-card {
    color: var(--color-txt-body);
    background: var(--color-surface-card)
}
[data-theme="dark"] &-content--name-title {
    color: var(--color-txt-body)
}
[data-theme="dark"] &--lang {
    background: var(--color-surface-card)
}
</style>